import { z } from 'zod';
import { Api } from '@automapa/svelte-automapa-template';
import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
import { CountSchema } from '../Vehicles/types';

// https://lm.nemo.indigo/docs/#req_7248d734dd034439b072ac1076faea90

export const TacticalPlanRequestSchema = z.object({
    name: z.string().catch(''),
    week_schedule: z.number().catch(0),
});
export type TacticalPlanRequestType = z.infer<typeof TacticalPlanRequestSchema>;

export const TacticalPlanResponseSchema = TacticalPlanRequestSchema.merge(
    z.object({
        id: z.coerce.number(),
        created: z.string(),
        updated: z.string(),
        revision: z.string().catch(''),
    })
);
export type TacticalPlanResponseType = z.infer<typeof TacticalPlanResponseSchema>;

export const TacticalPlanJsonImportResultSchema = z.object({
    imported: z.boolean(),
    tactical_plan_id: z.number().nullable(),
    name: z.string(),
    rules_count: z.number(),
    maps_count: z.number().catch(0),
    zones_count: z.number().catch(0),
    errors: z.array(z.string()),
    warnings: z.array(z.string()),
});
export type TacticalPlanJsonImportResultType = z.infer<typeof TacticalPlanJsonImportResultSchema>;

// plik dla importFromJson - passthrough, żeby żadne pole planu, reguł ani stref nie zginęło po drodze
export const TacticalPlanJsonExportSchema = z
    .object({
        original_id: z.number(),
        name: z.string(),
        rules: z.array(z.unknown()),
        maps: z.array(
            z.object({ original_id: z.number(), zones: z.array(z.unknown()) }).passthrough()
        ),
    })
    .passthrough();
export type TacticalPlanJsonExportType = z.infer<typeof TacticalPlanJsonExportSchema>;

export class TacticalPlanApi {
    public static async getAll(api: Api): Promise<TacticalPlanResponseType[]> {
        return api.getData<undefined, TacticalPlanResponseType[]>(
            'get',
            `/tactical_plan`,
            undefined,
            z.undefined(),
            z.array(TacticalPlanResponseSchema)
        );
    }

    public static async getOne(api: Api, id: number): Promise<TacticalPlanResponseType> {
        return api.getData<undefined, TacticalPlanResponseType>(
            'get',
            `/tactical_plan/${id}`,
            undefined,
            z.undefined(),
            TacticalPlanResponseSchema
        );
    }

    public static async filter(
        api: Api,
        filter: ApiFilterBuilder
    ): Promise<TacticalPlanResponseType[]> {
        return api.getData<undefined, TacticalPlanResponseType[]>(
            'get',
            `/tactical_plan/` + filter.getUri(),
            undefined,
            z.undefined(),
            z.array(TacticalPlanResponseSchema)
        );
    }

    public static async createOne(
        api: Api,
        req: TacticalPlanRequestType
    ): Promise<TacticalPlanResponseType> {
        return api.getData<TacticalPlanRequestType, TacticalPlanResponseType>(
            'post',
            `/tactical_plan`,
            req,
            TacticalPlanRequestSchema,
            TacticalPlanResponseSchema
        );
    }

    public static async deepcopy(
        api: Api,
        tacticalPlanId: number
    ): Promise<TacticalPlanResponseType> {
        return api.getData<Object, TacticalPlanResponseType>(
            'post',
            `/tactical_plan/${tacticalPlanId}/deepcopy`,
            {},
            z.object({}),
            TacticalPlanResponseSchema
        );
    }

    public static async updateOne(
        api: Api,
        id: number,
        req: TacticalPlanRequestType
    ): Promise<TacticalPlanResponseType> {
        return api.getData<TacticalPlanRequestType, TacticalPlanResponseType>(
            'put',
            `/tactical_plan/${id}`,
            req,
            TacticalPlanRequestSchema,
            TacticalPlanResponseSchema
        );
    }

    public static async removeOne(api: Api, id: number): Promise<void> {
        return api.delete(`/tactical_plan/${id}`);
    }

    public static async updateName(
        api: Api,
        id: number,
        name: string
    ): Promise<TacticalPlanResponseType> {
        return api.patch<TacticalPlanResponseType>(
            `/tactical_plan/${id}`,
            'replace',
            '/name',
            name,
            TacticalPlanResponseSchema
        );
    }

    public static async updateWeekSchedule(
        api: Api,
        id: number,
        week_schedule: number
    ): Promise<TacticalPlanResponseType> {
        return api.patch<TacticalPlanResponseType>(
            `/tactical_plan/${id}`,
            'replace',
            '/week_schedule',
            week_schedule,
            TacticalPlanResponseSchema
        );
    }

    public static async getStats(
        api: Api,
        tacticalPlanId: number
    ): Promise<{ vehicles: number; zones: number }> {
        return api.getData<any, { vehicles: number; zones: number }>(
            'post',
            `/tactical_plan/${tacticalPlanId}/stats`,
            {},
            z.any(),
            z.object({ vehicles: z.number(), zones: z.number() })
        );
        // return this.getCountByActionType(api, tacticalPlanId, 'vehicle');
    }

    public static async importFromJson(
        api: Api,
        planData: unknown
    ): Promise<TacticalPlanJsonImportResultType> {
        return api.getData<{ plan_data: unknown }, TacticalPlanJsonImportResultType>(
            'post',
            `/tactical_plan/import_from_json`,
            { plan_data: planData },
            z.object({ plan_data: z.any() }),
            TacticalPlanJsonImportResultSchema
        );
    }

    public static async exportToJson(
        api: Api,
        tacticalPlanId: number
    ): Promise<TacticalPlanJsonExportType> {
        return api.getData<{ tactical_plan_id: number }, TacticalPlanJsonExportType>(
            'post',
            `/tactical_plan/export_to_json`,
            // id z routingu (/tactical-plan/:id) przychodzi jako string mimo typu number
            { tactical_plan_id: Number(tacticalPlanId) },
            z.object({ tactical_plan_id: z.number() }),
            TacticalPlanJsonExportSchema
        );
    }
}
